import { Table, Tag, Typography, Empty, Flex, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { ActionLogEntry } from '@/api/logs';
import { CopyableId } from '@/components/conversation-overview';
import { formatWhen } from '@/utils/dateFormat';

const { Text } = Typography;

/** Tag colour per audited action: creations green, updates blue. */
const ACTION_COLORS: Record<string, string> = {
  FLOW_CREATED: 'green',
  TEMPLATE_CREATED: 'green',
  FLOW_UPDATED: 'blue',
  TEMPLATE_UPDATED: 'blue'
};

export interface ActionLogsTableProps {
  entries: ActionLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number, pageSize: number) => void;
}

/**
 * Server-paginated audit trail: who created or updated which flow or template, and when.
 */
export function ActionLogsTable({ entries, total, page, pageSize, loading, onPageChange }: ActionLogsTableProps) {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();

  const columns: ColumnsType<ActionLogEntry> = [
    {
      title: t('logs.audit.columns.time'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (value: string) => <Text style={{ whiteSpace: 'nowrap' }}>{formatWhen(value)}</Text>
    },
    {
      title: t('logs.audit.columns.user'),
      dataIndex: 'user_email',
      key: 'user_email',
      width: 240
    },
    {
      title: t('logs.audit.columns.action'),
      dataIndex: 'action',
      key: 'action',
      width: 200,
      render: (action: string) => (
        <Tag color={ACTION_COLORS[action] ?? 'default'}>{t(`logs.audit.actions.${action}`, { defaultValue: action })}</Tag>
      )
    },
    {
      title: t('logs.audit.columns.entity'),
      dataIndex: 'id',
      key: 'entity',
      render: (value: string) => <CopyableId value={value} />
    }
  ];

  return (
    <Table
      rowKey={(entry) => `${entry.timestamp}-${entry.action}-${entry.id}`}
      columns={columns}
      dataSource={entries}
      loading={loading}
      scroll={{ x: 'max-content' }}
      pagination={{
        current: page,
        pageSize,
        total,
        showTotal: (count, range) => t('logs.pagination', { start: range[0], end: range[1], total: count }),
        onChange: onPageChange,
        style: { paddingInline: token.padding }
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Flex vertical align="center" gap={token.marginXXS}>
                <Text>{t('logs.audit.empty')}</Text>
                <Text type="secondary">{t('logs.audit.emptyHint')}</Text>
              </Flex>
            }
          />
        )
      }}
    />
  );
}
